import React from "react";
import {getGraphDayState, getRealBsiItems, getReasonAbbreviation, getReasonStripeBackground} from "../../controlRequests/utils/graphDay";

function dayWidthBrowser() {
    const bw = navigator.userAgent.includes('(X11; Linux x86_64)')
    if (bw) {
        return 42
    } else return 37
}

export const width = dayWidthBrowser()

export const CustomBushCellRenderer: React.FC<any> = (params) => {
    const value = params.value
    const dayKey = params.colDef?.field?.split('.')[0]
    const dayData = dayKey ? params.data?.[dayKey] : null
    const style = getGraphDayStyle(dayData, value)
    const label = getGraphDayLabel(dayData, value)

    if (!style) {
        return null;
    }

    return (
        <div
            aria-label={`Plan ${value}`}
            style={{
                width: '100%',
                height: '100%',
                minHeight: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                color: getGraphDayTextColor(dayData, value),
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                ...style,
            }}
        >
            {label}
        </div>
    );
};

const WellCellRenderer: React.FC<any> = (params) => (
    <button
        type="button"
        className="graph-well-history-button"
        onClick={(event) => {
            event.stopPropagation()
            params.context?.openWellHistory?.(params.data)
        }}
        title={`Открыть полный график скважины ${params.value}`}
    >
        {params.value}
    </button>
)

function getGraphDayStyle(dayData: any, value: any): React.CSSProperties | null {
    const fallbackStyle = getPlanCodeStyle(value)

    if (!dayData || typeof dayData !== 'object') {
        return fallbackStyle
    }

    const dayState = getGraphDayState(dayData)

    if (dayState.kind === 'done_planned' || dayState.kind === 'done') {
        return {background: '#81c784'}
    }

    if (dayState.kind === 'manual_planned') {
        return {background: '#2e7d32'}
    }

    if (dayState.kind === 'manual_outside_plan') {
        return {
            background: 'linear-gradient(to bottom right, #2e7d32 0 calc(50% - 0.5px), #ffffff calc(50% + 0.5px) 100%)',
        }
    }

    if (dayState.kind === 'done_outside_plan') {
        return {
            background: 'linear-gradient(to bottom right, #81c784 0 calc(50% - 0.5px), #ffffff calc(50% + 0.5px) 100%)',
        }
    }

    if (dayState.kind === 'ppk_pending') {
        return {
            background: 'linear-gradient(to bottom right, #fff176 0 calc(50% - 0.5px), #66bb6a calc(50% + 0.5px) 100%)',
        }
    }

    if (dayState.kind === 'no_pass') {
        return {background: '#fff176'}
    }

    if (dayState.kind === 'reason') {
        return {
            background: getReasonStripeBackground(dayState.reason),
        }
    }

    if (dayState.kind === 'planned') {
        return {background: '#b3e5fc'}
    }

    return fallbackStyle
}

function getPlanCodeStyle(value: any): React.CSSProperties | null {
    const styleByValue: Record<number, React.CSSProperties> = {
        0: {background: '#ffffff'},
        1: {background: '#b3e5fc'},
        2: {background: '#81c784'},
        3: {
            background: 'linear-gradient(to bottom right, #81c784 0 calc(50% - 0.5px), #ffffff calc(50% + 0.5px) 100%)',
        },
        4: {background: '#fff176'},
        5: {background: '#2e7d32'},
    }

    return styleByValue[Number(value)] || null
}

function getGraphDayLabel(dayData: any, value: any): string {
    if (!dayData || typeof dayData !== 'object') {
        return ''
    }

    const dayState = getGraphDayState(dayData)

    if (dayState.kind === 'reason') {
        return getReasonAbbreviation(dayState.reason)
    }

    const shouldShowDepth = [
        'done_planned',
        'done',
        'done_outside_plan',
        'manual_planned',
        'manual_outside_plan',
        'no_pass',
    ].includes(dayState.kind) || [2, 3, 4, 5].includes(Number(value))

    if (!shouldShowDepth) {
        return ''
    }

    const depth = Number(dayState.maxDepth || getRawDayDepth(dayData) || 0)

    if (!depth) {
        return ''
    }

    return String(Math.round(depth))
}

function getRawDayDepth(dayData: any): number {
    const bsiDepth = getRealBsiItems(dayData).reduce((max: number, item: any) => (
        Math.max(max, Number(item?.depth || 0))
    ), 0)

    return bsiDepth || Number(dayData?.depth || 0)
}

function getGraphDayTextColor(dayData: any, value: any): string {
    if (!dayData || typeof dayData !== 'object') {
        return Number(value) === 5 ? '#ffffff' : '#1b5e20'
    }

    const dayState = getGraphDayState(dayData)

    if (dayState.kind === 'no_pass') {
        return '#795548'
    }

    if (dayState.kind === 'reason') {
        return '#475467'
    }

    if (dayState.kind === 'ppk_pending') {
        return '#263238'
    }

    if (['manual_planned'].includes(dayState.kind) || Number(value) === 5) {
        return '#ffffff'
    }

    return '#1b5e20'
}

const dayColumnDefaults = {
    width: width,
    maxWidth: width,
    resizable: false,
    suppressMenu: true,
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: true,
    cellClass: 'graph-plan-day-cell',
    cellRenderer: CustomBushCellRenderer,
}

export const columnDefs = [
    {
        field: 'workDate',
        headerName: 'Дата',
        hide: true,
        enablePivot: true,
    },
    {
        field: 'location',
        headerName: 'Локация',
        initialWidth: 70,
        sortable: true,
        filter: true,
        resizable: true,
        enablePivot: true,
        rowGroupIndex: 1,
        hide: true,

    },
    {
        field: 'deposit',
        headerName: 'Месторождение',
        initialWidth: 90,
        sortable: true,
        filter: true,
        resizable: true,
        enablePivot: true,
        hide: true,
    },
    {
        field: 'dns',
        headerName: 'ДНС',
        initialWidth: 70,
        sortable: true,
        filter: true,
        enablePivot: true,
        enableRowGroup: true,
        hide: true,
    },
    {
        field: 'bush',
        headerName: 'Куст',
        initialWidth: 70,
        sortable: true,
        enablePivot: true,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
    },
    {
        field: 'wells.name',
        headerName: 'Скважина',
        initialWidth: 90,
        sortable: true,
        filter: 'agSetColumnFilter',
        resizable: true,
        floatingFilter: true,
        cellRenderer: WellCellRenderer,
    },
    {
        field: 'org',
        headerName: 'Исполнитель',
        minWidth: 100,
        initialWidth: 100,
        sortable: true,
        filter: true,
        resizable: true,
        enableRowGroup: true,
        enablePivot: true,
        hide: localStorage.getItem('username') !== 'admin'
    },
    {
        field: 'diam',
        headerName: 'Диаметр Ø',
        initialWidth: 40,
        sortable: true,
        filter: 'agSetColumnFilter',
        resizable: true,
        enableRowGroup: true,
        floatingFilter: true,
        enablePivot: true,
    },
    {
        field: 'd1.plan',
        headerName: '1',
        ...dayColumnDefaults,
        tooltipField: 'd1.plan',
    },
    {
        field: 'd2.plan',
        headerName: '2',
        ...dayColumnDefaults,
        tooltipField: 'd2.plan',
    },
    {
        field: 'd3.plan',
        headerName: '3',
        ...dayColumnDefaults,
        tooltipField: 'd3.plan',

    },
    {
        field: 'd4.plan',
        headerName: '4',
        ...dayColumnDefaults,
        tooltipField: 'd4.plan',

    },
    {
        field: 'd5.plan',
        headerName: '5',
        ...dayColumnDefaults,
        tooltipField: 'd5.plan',

    },
    {
        field: 'd6.plan',
        headerName: '6',
        ...dayColumnDefaults,
        tooltipField: 'd6.plan',

    },
    {
        field: 'd7.plan',
        headerName: '7',
        ...dayColumnDefaults,
        tooltipField: 'd7.plan',

    },
    {
        field: 'd8.plan',
        headerName: '8',
        ...dayColumnDefaults,
        tooltipField: 'd8.plan',

    },
    {
        field: 'd9.plan',
        headerName: '9',
        ...dayColumnDefaults,
        tooltipField: 'd9.plan',

    },
    {
        field: 'd10.plan',
        headerName: '10',
        ...dayColumnDefaults,
        tooltipField: 'd10.plan',

    },
    {
        field: 'd11.plan',
        headerName: '11',
        ...dayColumnDefaults,
        tooltipField: 'd11.plan',

    },
    {
        field: 'd12.plan',
        headerName: '12',
        ...dayColumnDefaults,
        tooltipField: 'd12.plan',

    },
    {
        field: 'd13.plan',
        headerName: '13',
        ...dayColumnDefaults,
        tooltipField: 'd13.plan',

    },
    {
        field: 'd14.plan',
        headerName: '14',
        ...dayColumnDefaults,
        tooltipField: 'd14.plan',

    },
    {
        field: 'd15.plan',
        headerName: '15',
        ...dayColumnDefaults,
        tooltipField: 'd15.plan',

    },
    {
        field: 'd16.plan',
        headerName: '16',
        ...dayColumnDefaults,
        tooltipField: 'd16.plan',

    },
    {
        field: 'd17.plan',
        headerName: '17',
        ...dayColumnDefaults,
        tooltipField: 'd17.plan',

    },
    {
        field: 'd18.plan',
        headerName: '18',
        ...dayColumnDefaults,
        tooltipField: 'd18.plan',

    },
    {
        field: 'd19.plan',
        headerName: '19',
        ...dayColumnDefaults,
        tooltipField: 'd19.plan',

    },
    {
        field: 'd20.plan',
        headerName: '20',
        ...dayColumnDefaults,
        tooltipField: 'd20.plan',

    },
    {
        field: 'd21.plan',
        headerName: '21',
        ...dayColumnDefaults,
        tooltipField: 'd21.plan',

    },
    {
        field: 'd22.plan',
        headerName: '22',
        ...dayColumnDefaults,
        tooltipField: 'd22.plan',

    },
    {
        field: 'd23.plan',
        headerName: '23',
        ...dayColumnDefaults,
        tooltipField: 'd23.plan',

    },
    {
        field: 'd24.plan',
        headerName: '24',
        ...dayColumnDefaults,
        tooltipField: 'd24.plan',

    },
    {
        field: 'd25.plan',
        headerName: '25',
        ...dayColumnDefaults,
        tooltipField: 'd25.plan',

    },
    {
        field: 'd26.plan',
        headerName: '26',
        ...dayColumnDefaults,
        tooltipField: 'd26.plan',

    },
    {
        field: 'd27.plan',
        headerName: '27',
        ...dayColumnDefaults,
        tooltipField: 'd27.plan',

    },
    {
        field: 'd28.plan',
        headerName: '28',
        ...dayColumnDefaults,
        tooltipField: 'd28.plan',

    },
    {
        field: 'd29.plan',
        headerName: '29',
        ...dayColumnDefaults,
        tooltipField: 'd29.plan',

    },
    {
        field: 'd30.plan',
        headerName: '30',
        ...dayColumnDefaults,
        tooltipField: 'd30.plan',

    },
    {
        field: 'd31.plan',
        headerName: '31',
        ...dayColumnDefaults,
        tooltipField: 'd31.plan',

    },
    {
        field: 'total_plan',
        headerName: 'План',
        // width: 50,
        // maxWidth: 50,
        initialWidth: 55,
        resizable: true,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        aggFunc: 'sum'
    },
    {
        field: 'total_fact',
        headerName: 'Факт',
        // width: 50,
        // maxWidth: 50,
        initialWidth: 50,
        resizable: true,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        aggFunc: 'sum'
    },
    {
        field: 'bsi_level',
        headerName: 'БСИ',
        // width: 70,
        // maxWidth: 70,
        initialWidth: 70,
        resizable: true,
        sortable: true,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
    },
    {
        field: 'level',
        headerName: 'Степень',
        // width: 70,
        // maxWidth: 70,
        initialWidth: 70,
        resizable: true,
        sortable: true,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
    },
       {
        field: 'comment',
        headerName: 'Примечание',
        // width: 70,
        // maxWidth: 70,
        initialWidth: 70,
        resizable: true,
        sortable: true,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
    },
];
